import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { NotificationComponent } from '../../common/notification/notification.component';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  MainBannerInitModel,
  MainBannerInitVariables,
  MainBannerModel,
  MainBannerVariables,
} from '../../../../models/email-template/main-banner.interface';

/**
 * Mockup-styled Main Banner block - a drop-in replacement for app-main-banner
 * in block-renderer-v2 (same [block]/[isPreview] inputs and outputs), so it
 * covers both the center-canvas read-only summary (isPreview=true) and the
 * right-panel edit form (isPreview=false). Reuses the real, unmodified
 * app-block-text (incl. its image upload, segment dropdown and validation
 * logic), app-notification and app-pin sub-components and the exact same
 * MainBannerVariables field names - only the surrounding layout is new.
 */
@Component({
  selector: 'app-main-banner-v2',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BlockTextComponent,
    FieldEditorV2Component,
    NotificationComponent,
    InlineMediaV2Directive,
  ],
  templateUrl: './main-banner-v2.component.html',
  styleUrl: './main-banner-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MainBannerComponentV2 extends BaseComponent implements OnInit {
  @Input() block: MainBannerModel = new MainBannerInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;
  /** Center-canvas summary shows the desktop OR mobile image to match the
   *  canvas's own device toggle, not both stacked. */
  @Input() deviceMode: 'desktop' | 'mobile' = 'desktop';

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new MainBannerInitVariables();
    }
  }

  imageDesktopPlaceholder = 'Desktop Image';
  imageMobilePlaceholder = 'Mobile Image';
  hrefPlaceholder = 'Enter Image Redirect Link';
  altPlaceholder = 'Enter Image Alt Text';

  /** Drives app-block-text's own [isImage] input from outside (its internal
   *  toggle checkbox is hidden via CSS - see the component's scss) so the
   *  field-row switch can flip between the upload widget and a plain text/
   *  link input for pasting an image URL directly, per field. */
  protected desktopImageAsUpload = true;
  protected mobileImageAsUpload = true;

  protected toggleDesktopImageMode(): void {
    this.desktopImageAsUpload = !this.desktopImageAsUpload;
  }

  protected toggleMobileImageMode(): void {
    this.mobileImageAsUpload = !this.mobileImageAsUpload;
  }

  /** The image matching the canvas's current device toggle - undefined if
   *  that specific slot (desktop/mobile) hasn't been uploaded, even when the
   *  other one has. */
  get currentDeviceImage(): string | undefined {
    return this.block.variables?.[this.currentDeviceImageKey];
  }

  /** The variable the canvas image slot shows and replaces - an upload in
   *  mobile view replaces the mobile image, not the desktop one. */
  get currentDeviceImageKey(): 'MainBannerImageMobile' | 'MainBannerImageDesktop' {
    return this.deviceMode === 'mobile' ? 'MainBannerImageMobile' : 'MainBannerImageDesktop';
  }

  /** The redirect link opened by clicking the preview image - v1's exact
   *  `helper.getFullUrl(MainBannerHref)`, plus a `segment` query param when a
   *  segment is selected (MainBannerHrefSegmentRegex is otherwise unused
   *  metadata today - not read anywhere else in the app, v1 included), so a
   *  segment-targeted banner can be click-tested straight from the preview. */
  get previewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['MainBannerHref']);
    const segment = this.block.variables?.['MainBannerHrefSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof MainBannerVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
